/**
 * Transition Studio Modal - Unified Transition Experience
 * 
 * Modern modal combining preview + editing in one place.
 * Hero preview with floating controls + sliding property dock.
 * Supports both legacy video elements and Remotion-based TransitionCompositorPlayer.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Modal, Button, Typography, Slider, Spin } from 'antd';
import { 
  Play, Pause, RotateCcw, Settings, X, Check,
  Zap, TrendingDown, Sparkles, MoveRight, ChevronRight
} from 'lucide-react';
import { Scene, SceneTransition, TransitionType } from '../../../../types/scene';
import SceneService from '../../services/sceneService';
import { 
  DEFAULT_TRANSITION_DURATIONS, 
  MIN_TRANSITION_DURATIONS, 
  MAX_TRANSITION_DURATIONS,
  validateTransitionDuration
} from '../../constants/transitionConstants';
import { TransitionCompositorPlayer, TransitionCompositorPlayerRef } from '../../compositor/components/TransitionCompositorPlayer';
import { CompositorRecordingData } from '../../compositor/types/manifest';
import { getPendingRecordingBySceneId } from '../../idb/chunk-db';
import { buildRecordingDataFromScene } from '../../compositor/utils/sceneToRecordingData';
import logger from '../../utils/logger';
import './TransitionStudioModal.scss';

const { Text } = Typography;

// Module-level cache that persists across component mounts/remounts
// Key is "fromSceneId-toSceneId", value is cached data
interface TransitionCache {
  useCompositor: boolean;
  bothScenesRecorded: boolean;
  fromRecordingData: CompositorRecordingData | null;
  toRecordingData: CompositorRecordingData | null;
}
const transitionCacheMap = new Map<string, TransitionCache>();

function getTransitionCacheKey(fromId: string | undefined, toId: string | undefined): string {
  return `${fromId || 'null'}-${toId || 'null'}`;
}

function hasSeparateStreamRecordingMetadata(sceneMetadata: any): boolean {
  if (!sceneMetadata) return false;
  try {
    const metadata = typeof sceneMetadata === 'string' 
      ? JSON.parse(sceneMetadata) 
      : sceneMetadata;
    if (metadata.recordingType !== 'separate_streams') return false;
    const streams = metadata.streams;
    if (!streams) return false;
    const hasWebcamWithKeys = streams.webcam?.segments?.some((s: any) => s.key);
    const hasScreenWithKeys = streams.screen?.segments?.some((s: any) => s.key);
    return hasWebcamWithKeys || hasScreenWithKeys;
  } catch {
    return false;
  }
}


interface TransitionStudioModalProps {
  visible: boolean;
  transition: SceneTransition;
  onClose: () => void;
  onChange: (transition: SceneTransition) => void;
  onApplyToAll?: (transition: SceneTransition) => void;
  onApplyToRemaining?: (transition: SceneTransition) => void;
  fromScene?: Scene;
  toScene?: Scene;
  projectId?: string;
  isFirstTransition?: boolean;
  isLastTransition?: boolean;
  beforeSceneDuration?: number;
  afterSceneDuration?: number;
  disabled?: boolean;
}

const TRANSITION_OPTIONS = [
  { 
    value: 'cut' as TransitionType, 
    label: 'Instant', 
    icon: <Zap size={18} />,
    description: 'No transition',
  },
  { 
    value: 'fade' as TransitionType, 
    label: 'Fade', 
    icon: <TrendingDown size={18} />,
    description: 'Fade through black',
  },
  { 
    value: 'crossfade' as TransitionType, 
    label: 'Crossfade', 
    icon: <Sparkles size={18} />,
    description: 'Smooth dissolve',
  },
  { 
    value: 'slide' as TransitionType, 
    label: 'Slide', 
    icon: <MoveRight size={18} />,
    description: 'Slide transition',
  },
];

const getTransitionIcon = (type: TransitionType) => {
  switch (type) {
    case 'cut': return <Zap size={16} />;
    case 'fade': return <TrendingDown size={16} />;
    case 'crossfade': return <Sparkles size={16} />;
    case 'slide': return <MoveRight size={16} />;
    default: return <Zap size={16} />;
  }
};

const TransitionStudioModal: React.FC<TransitionStudioModalProps> = ({
  visible,
  transition,
  onClose,
  onChange,
  onApplyToAll,
  onApplyToRemaining,
  fromScene,
  toScene,
  projectId,
  isFirstTransition = false,
  isLastTransition = false,
  beforeSceneDuration,
  afterSceneDuration,
  disabled = false,
}) => {
  const [showPropertyDock, setShowPropertyDock] = useState(false);
  
  const [localTransition, setLocalTransition] = useState<SceneTransition>(() => ({
    type: transition?.type || 'cut',
    duration: transition?.duration ?? DEFAULT_TRANSITION_DURATIONS[transition?.type || 'cut'],
    easing: transition?.easing || 'ease-in-out'
  }));

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [compositorReady, setCompositorReady] = useState(false);
  
  const [fromVideoUrl, setFromVideoUrl] = useState<string | null>(null);
  const [toVideoUrl, setToVideoUrl] = useState<string | null>(null);
  const [fromVideoDuration, setFromVideoDuration] = useState<number>(0);
  const [toVideoDuration, setToVideoDuration] = useState<number>(0);
  const [videosReady, setVideosReady] = useState(false);
  
  // Pending blob URLs for unconfirmed scenes (local IndexedDB data)
  const [fromPendingBlobUrls, setFromPendingBlobUrls] = useState<Map<string, string>>(new Map());
  const [toPendingBlobUrls, setToPendingBlobUrls] = useState<Map<string, string>>(new Map());
  
  // IDB-loaded recording data for pending scenes (overrides scene.metadata)
  const [fromIdbRecordingData, setFromIdbRecordingData] = useState<CompositorRecordingData | null>(null);
  const [toIdbRecordingData, setToIdbRecordingData] = useState<CompositorRecordingData | null>(null);
  
  const fromVideoRef = useRef<HTMLVideoElement>(null);
  const toVideoRef = useRef<HTMLVideoElement>(null);
  const compositorRef = useRef<TransitionCompositorPlayerRef>(null);
  
  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  
  // Use module-level cache that persists across component mounts/remounts
  // This is critical because after transition save, the project reloads and 
  // scenes temporarily lose their metadata during the reload
  const cacheKey = getTransitionCacheKey(fromScene?.id, toScene?.id);
  
  // Get or create cache entry
  const getCache = useCallback((): TransitionCache => {
    return transitionCacheMap.get(cacheKey) || {
      useCompositor: false,
      bothScenesRecorded: false,
      fromRecordingData: null,
      toRecordingData: null,
    };
  }, [cacheKey]);
  
  const updateCache = useCallback((updates: Partial<TransitionCache>) => {
    const current = getCache();
    transitionCacheMap.set(cacheKey, { ...current, ...updates });
  }, [cacheKey, getCache]);

  const useCompositor = useMemo(() => {
    // First check module-level cache
    const cache = getCache();
    if (cache.useCompositor) {
      return true;
    }
    
    const fromMetadata = (fromScene as any)?.metadata;
    const toMetadata = (toScene as any)?.metadata;
    const fromHasSeparateStreams = fromScene ? hasSeparateStreamRecordingMetadata(fromMetadata) : false;
    const toHasSeparateStreams = toScene ? hasSeparateStreamRecordingMetadata(toMetadata) : false;
    const result = fromHasSeparateStreams || toHasSeparateStreams;
    
    // Cache the result if true
    if (result) {
      updateCache({ useCompositor: true });
    }
    return result;
  }, [fromScene, toScene, getCache, updateCache]);

  const [freshFromRecordingData, setFreshFromRecordingData] = useState<CompositorRecordingData | null>(null);
  const [freshToRecordingData, setFreshToRecordingData] = useState<CompositorRecordingData | null>(null);
  const [compositorDataReady, setCompositorDataReady] = useState(false);

  useEffect(() => {
    if (!visible || !useCompositor) {
      setFreshFromRecordingData(null);
      setFreshToRecordingData(null);
      setCompositorDataReady(false);
      return;
    }

    let cancelled = false;
    setCompositorDataReady(false);

    const fetchFreshData = async () => {
      const fetchScene = async (scene: Scene | undefined) => {
        if (!scene) return null;
        try {
          const freshScene = await SceneService.getSceneById(String(scene.id), projectId);
          if (freshScene && !cancelled) {
            return buildRecordingDataFromScene(freshScene);
          }
        } catch (err) {
          logger.warn('[TransitionStudioModal] Failed to fetch fresh scene, using prop data:', err);
        }
        return buildRecordingDataFromScene(scene);
      };

      const [fromData, toData] = await Promise.all([
        fetchScene(fromScene),
        fetchScene(toScene),
      ]);

      if (!cancelled) {
        setFreshFromRecordingData(fromData);
        setFreshToRecordingData(toData);
        setCompositorDataReady(true);
      }
    };

    fetchFreshData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, useCompositor, fromScene?.id, toScene?.id, projectId]);

  const fromRecordingData = useMemo(() => {
    if (fromIdbRecordingData) {
      return fromIdbRecordingData;
    }
    
    if (freshFromRecordingData) {
      return freshFromRecordingData;
    }

    if (!useCompositor || !fromScene) {
      const cache = getCache();
      if (cache.fromRecordingData) {
        return cache.fromRecordingData;
      }
      return null;
    }

    return null;
  }, [useCompositor, fromScene, fromIdbRecordingData, freshFromRecordingData, getCache]);

  const toRecordingData = useMemo(() => {
    if (toIdbRecordingData) {
      return toIdbRecordingData;
    }
    
    if (freshToRecordingData) {
      return freshToRecordingData;
    }

    if (!useCompositor || !toScene) {
      const cache = getCache();
      if (cache.toRecordingData) {
        return cache.toRecordingData;
      }
      return null;
    }

    return null;
  }, [useCompositor, toScene, toIdbRecordingData, freshToRecordingData, getCache]);

  const sceneHasRecording = useCallback((scene: Scene | undefined): boolean => {
    if (!scene) {
      return false;
    }
    // 'recording' status means actively being recorded - not yet done
    if (scene.status === 'recording') {
      return false;
    }
    
    // Check for pending statuses that indicate scene HAS a recording
    // 'pending', 'pending_confirmation', 'pending_upload' all mean recording exists locally
    const isPendingStatus = scene.status === 'pending' || 
                           scene.status === 'pending_confirmation' || 
                           scene.status === 'pending_upload';
    
    if (isPendingStatus) {
      // For pending scenes, check metadata for duration/streams instead of scene.duration
      // The scene.duration might be 0 but metadata has the real data
      const sceneWithMeta = scene as any;
      if (sceneWithMeta.metadata) {
        try {
          const metadata = typeof sceneWithMeta.metadata === 'string' 
            ? JSON.parse(sceneWithMeta.metadata) 
            : sceneWithMeta.metadata;
          
          // Check if metadata has duration or streams
          const hasDurationInMetadata = metadata.duration && metadata.duration > 0;
          const hasStreams = metadata.streams?.webcam?.segments?.length > 0 || 
                            metadata.streams?.screen?.segments?.length > 0;
          
          if (hasDurationInMetadata || hasStreams) {
            return true;
          }
        } catch (e) {
          console.error('[TransitionStudioModal] sceneHasRecording pending metadata parse error:', e);
        }
      }
      // Even without good metadata, pending status with recordingType indicates recording exists
      return false;
    }
    
    if (scene.duration === 0) {
      return false;
    }
    const sceneWithMeta = scene as any;
    if (!sceneWithMeta.metadata) {
      return false;
    }
    try {
      const metadata = typeof sceneWithMeta.metadata === 'string' 
        ? JSON.parse(sceneWithMeta.metadata) 
        : sceneWithMeta.metadata;
      if (metadata.recordingType === 'separate_streams') {
        // Check if segments have S3 key OR have pending upload status (local data exists)
        const hasWebcamWithKey = metadata.streams?.webcam?.segments?.some((s: any) => s.key);
        const hasScreenWithKey = metadata.streams?.screen?.segments?.some((s: any) => s.key);
        const hasWebcamPending = metadata.streams?.webcam?.segments?.some((s: any) => s.uploadStatus === 'pending');
        const hasScreenPending = metadata.streams?.screen?.segments?.some((s: any) => s.uploadStatus === 'pending');
        // Scene has recording if it has uploaded segments OR pending segments (local data)
        const result = hasWebcamWithKey || hasScreenWithKey || hasWebcamPending || hasScreenPending;
        return result;
      }
      return true;
    } catch (e) {
      console.error('[TransitionStudioModal] sceneHasRecording error:', e);
      return false;
    }
  }, []);

  // Check if both scenes have recordings - use module-level cache
  const bothScenesRecorded = useMemo(() => {
    const cache = getCache();
    
    // If we've cached that both scenes are recorded, use that
    if (cache.bothScenesRecorded) {
      return true;
    }
    
    const fromHas = sceneHasRecording(fromScene);
    const toHas = sceneHasRecording(toScene);
    const result = fromHas && toHas;
    
    // Cache if true
    if (result) {
      updateCache({ bothScenesRecorded: true });
    }
    
    return result;
  }, [fromScene, toScene, sceneHasRecording, getCache, updateCache, cacheKey]);

  const beforeSeconds = beforeSceneDuration ? beforeSceneDuration / 1000 : 5;
  const afterSeconds = afterSceneDuration ? afterSceneDuration / 1000 : 5;
  const CLIP_DURATION = Math.min(Math.max(Math.min(beforeSeconds, afterSeconds) * 0.5, 2), 5);
  const transitionDuration = localTransition.type === 'cut' ? 0.5 : (localTransition.duration || 1.0);
  const totalDuration = CLIP_DURATION + transitionDuration + CLIP_DURATION;

  useEffect(() => {
    if (transition && visible) {
      setLocalTransition({
        type: transition.type || 'cut',
        duration: transition.duration ?? DEFAULT_TRANSITION_DURATIONS[transition.type || 'cut'],
        easing: transition.easing || 'ease-in-out'
      });
    }
  }, [transition, visible]);

  // Load pending blobs from IndexedDB for unconfirmed scenes
  useEffect(() => {
    if (!visible || !useCompositor) {
      return;
    }
    
    interface PendingLoadResult {
      blobUrls: Map<string, string>;
      recordingData: CompositorRecordingData | null;
    }
    
    const loadPendingBlobs = async (sceneId: string, sceneName: string, sceneBackground?: any): Promise<PendingLoadResult> => {
      const blobUrlsMap = new Map<string, string>();
      let recordingData: CompositorRecordingData | null = null;
      
      try {
        const pendingRow = await getPendingRecordingBySceneId(sceneId);
        
        if (!pendingRow) {
          return { blobUrls: blobUrlsMap, recordingData: null };
        }
        
        // Load webcam segment blobs
        if (pendingRow.webcamSegments) {
          pendingRow.webcamSegments.forEach((segment: any, index: number) => {
            if (segment.blob) {
              blobUrlsMap.set(`webcam-${segment.segmentIndex ?? index}`, URL.createObjectURL(segment.blob));
            }
          });
        }
        
        // Load screen segment blobs
        if (pendingRow.screenSegments) {
          pendingRow.screenSegments.forEach((segment: any, index: number) => {
            if (segment.blob) {
              blobUrlsMap.set(`screen-${segment.segmentIndex ?? index}`, URL.createObjectURL(segment.blob));
            }
          });
        }
        
        // Load audio segment blobs
        if (pendingRow.audioSegments) {
          pendingRow.audioSegments.forEach((segment: any, index: number) => {
            if (segment.blob) {
              blobUrlsMap.set(`audio-${segment.segmentIndex ?? index}`, URL.createObjectURL(segment.blob));
            }
          });
        }
        
        // Build recording data from IDB pending row (this represents the NEW recording)
        if (pendingRow.layout && pendingRow.duration) {
          const layout = pendingRow.layout;
          // Background priority: layout.background (actual canvas at recording time) > default
          // layout.background is the CORRECT source - captured directly from canvas when recording started
          // sceneBackground is just the template default and should NOT be used
          let resolvedBackground: any = null;
          
          // Use layout.background (what was actually on canvas when recording)
          if (layout.background) {
            resolvedBackground = layout.background;
          }
          
          // Final fallback
          if (!resolvedBackground) {
            resolvedBackground = { type: 'color', value: '#1a1a2e' };
          }
          
          recordingData = {
            duration: pendingRow.duration,
            layout: {
              canvas: layout.canvas || { width: 1920, height: 1080, aspectRatio: '16:9' },
              webcam: layout.webcam,
              screen: layout.screen,
              background: resolvedBackground,
            },
            streams: {
              webcam: pendingRow.webcamSegments?.length ? {
                segments: pendingRow.webcamSegments.map((seg: any) => ({
                  segmentIndex: seg.segmentIndex,
                  tsStart: seg.tsStart,
                  tsEnd: seg.tsEnd ?? pendingRow.duration,
                  hasAudio: seg.hasAudio ?? false,
                  uploadStatus: 'pending',
                  x: seg.x ?? layout.webcam?.x ?? 0,
                  y: seg.y ?? layout.webcam?.y ?? 0,
                  width: seg.width ?? layout.webcam?.width ?? 640,
                  height: seg.height ?? layout.webcam?.height ?? 480,
                  isCircle: seg.isCircle ?? layout.webcam?.isCircle ?? false,
                  cornerRadius: seg.cornerRadius ?? layout.webcam?.cornerRadius ?? 0,
                  isMirrored: seg.isMirrored ?? layout.webcam?.mirrored ?? false,
                  isAvatar: seg.isAvatar ?? layout.webcam?.isAvatar ?? false,
                  avatarUrl: seg.avatarUrl ?? layout.webcam?.avatarUrl ?? undefined,
                })),
              } : undefined,
              screen: pendingRow.screenSegments?.length ? {
                segments: pendingRow.screenSegments.map((seg: any) => ({
                  segmentIndex: seg.segmentIndex,
                  tsStart: seg.tsStart,
                  tsEnd: seg.tsEnd ?? pendingRow.duration,
                  hasAudio: seg.hasAudio ?? false,
                  uploadStatus: 'pending',
                  x: seg.x ?? layout.screen?.x ?? 0,
                  y: seg.y ?? layout.screen?.y ?? 0,
                  width: seg.width ?? layout.screen?.width ?? 1920,
                  height: seg.height ?? layout.screen?.height ?? 1080,
                  isCircle: seg.isCircle ?? layout.screen?.isCircle ?? false,
                  cornerRadius: seg.cornerRadius ?? layout.screen?.cornerRadius ?? 0,
                })),
              } : undefined,
              audio: pendingRow.audioSegments?.length ? {
                segments: pendingRow.audioSegments.map((seg: any) => ({
                  segmentIndex: seg.segmentIndex,
                  tsStart: seg.tsStart,
                  tsEnd: seg.tsEnd ?? pendingRow.duration,
                  hasMic: seg.hasMic ?? true,
                  hasSystemAudio: seg.hasSystemAudio ?? false,
                  isMuted: seg.isMuted ?? false,
                })),
              } : undefined,
            },
            version: '2.1',
          };
        }
      } catch (err) {
        logger.error(`[TransitionStudioModal] Error loading pending blobs for ${sceneName}:`, err);
      }
      return { blobUrls: blobUrlsMap, recordingData };
    };
    
    const loadAllPendingBlobs = async () => {
      // Check if fromScene needs pending blobs
      if (fromScene?.id) {
        const fromBackground = fromScene.background;
        const fromResult = await loadPendingBlobs(fromScene.id, 'fromScene', fromBackground);
        if (fromResult.blobUrls.size > 0) {
          setFromPendingBlobUrls(fromResult.blobUrls);
        }
        if (fromResult.recordingData) {
          setFromIdbRecordingData(fromResult.recordingData);
        }
      }
      
      // Check if toScene needs pending blobs
      if (toScene?.id) {
        const toBackground = toScene.background;
        const toResult = await loadPendingBlobs(toScene.id, 'toScene', toBackground);
        if (toResult.blobUrls.size > 0) {
          setToPendingBlobUrls(toResult.blobUrls);
        }
        if (toResult.recordingData) {
          setToIdbRecordingData(toResult.recordingData);
        }
      }
    };
    
    loadAllPendingBlobs();
    
    // Cleanup blob URLs on unmount
    return () => {
      fromPendingBlobUrls.forEach(url => URL.revokeObjectURL(url));
      toPendingBlobUrls.forEach(url => URL.revokeObjectURL(url));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, useCompositor, fromScene?.id, toScene?.id]);

  useEffect(() => {
    if (!visible) {
      if (fromVideoUrl) URL.revokeObjectURL(fromVideoUrl);
      if (toVideoUrl) URL.revokeObjectURL(toVideoUrl);
      // Clean up pending blob URLs
      fromPendingBlobUrls.forEach(url => URL.revokeObjectURL(url));
      toPendingBlobUrls.forEach(url => URL.revokeObjectURL(url));
      setFromVideoUrl(null);
      setToVideoUrl(null);
      setFromPendingBlobUrls(new Map());
      setToPendingBlobUrls(new Map());
      setFromIdbRecordingData(null);
      setToIdbRecordingData(null);
      setIsPlaying(false);
      setCurrentTime(0);
      setVideosReady(false);
      setFromVideoDuration(0);
      setToVideoDuration(0);
      setShowPropertyDock(false);
      setCompositorReady(false);
      return;
    }

    if (useCompositor) {
      setIsLoading(false);
      return;
    }

    const loadVideos = async () => {
      setIsLoading(true);
      try {
        if (fromScene?.id) {
          const fromBlob = await SceneService.getSceneVideoBlob(fromScene.id, projectId);
          if (fromBlob) setFromVideoUrl(URL.createObjectURL(fromBlob));
        }
        if (toScene?.id) {
          const toBlob = await SceneService.getSceneVideoBlob(toScene.id, projectId);
          if (toBlob) setToVideoUrl(URL.createObjectURL(toBlob));
        }
      } catch (err) {
        logger.error('[TransitionStudio] Error loading videos:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadVideos();

    return () => {
      if (fromVideoUrl) URL.revokeObjectURL(fromVideoUrl);
      if (toVideoUrl) URL.revokeObjectURL(toVideoUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, fromScene?.id, toScene?.id, projectId, useCompositor]);

  useEffect(() => {
    if (useCompositor) return;
    
    const hasFromVideo = fromVideoUrl && fromVideoDuration > 0;
    const hasToVideo = toVideoUrl && toVideoDuration > 0;
    const isReady = (hasFromVideo && hasToVideo) || 
                    (hasFromVideo && !toVideoUrl) || 
                    (hasToVideo && !fromVideoUrl) ||
                    (!fromVideoUrl && !toVideoUrl);
    
    if (isReady && !videosReady) {
      setVideosReady(true);
      if (fromVideoRef.current && fromVideoDuration > 0) {
        fromVideoRef.current.currentTime = Math.max(0, fromVideoDuration - CLIP_DURATION);
      }
      if (toVideoRef.current) {
        toVideoRef.current.currentTime = 0;
      }
    }
  }, [fromVideoUrl, toVideoUrl, fromVideoDuration, toVideoDuration, videosReady, CLIP_DURATION, useCompositor]);

  const autoPlayedRef = useRef(false);
  useEffect(() => {
    if (!visible) {
      autoPlayedRef.current = false;
      return;
    }
    
    const isReady = useCompositor ? (compositorDataReady && compositorReady) : (!isLoading && videosReady);
    
    if (visible && isReady && !autoPlayedRef.current && !isPlaying) {
      autoPlayedRef.current = true;
      const timer = setTimeout(() => {
        setCurrentTime(0);
        setIsPlaying(true);
        lastTimeRef.current = 0;
        if (useCompositor && compositorRef.current) {
          compositorRef.current.seekTo(0);
          compositorRef.current.play();
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isLoading, videosReady, isPlaying, useCompositor, compositorReady, compositorDataReady]);

  useEffect(() => {
    if (useCompositor) return;
    if (!visible || !isPlaying || isLoading) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const animate = (timestamp: number) => {
      if (lastTimeRef.current === 0) lastTimeRef.current = timestamp;
      const delta = (timestamp - lastTimeRef.current) / 1000;
      lastTimeRef.current = timestamp;

      setCurrentTime((prev) => {
        const next = prev + delta;
        if (next >= totalDuration) {
          setIsPlaying(false);
          return totalDuration;
        }
        return next;
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
  }, [visible, isPlaying, totalDuration, isLoading, useCompositor]);

  useEffect(() => {
    if (useCompositor) return;
    if (!fromVideoDuration || !toVideoDuration) return;

    if (currentTime < CLIP_DURATION) {
      if (fromVideoRef.current) {
        const videoTime = Math.max(0, fromVideoDuration - CLIP_DURATION + currentTime);
        fromVideoRef.current.currentTime = Math.min(videoTime, fromVideoDuration - 0.01);
        fromVideoRef.current.volume = 1;
      }
      if (toVideoRef.current) {
        toVideoRef.current.currentTime = 0;
        toVideoRef.current.volume = 0;
      }
    } else if (currentTime < CLIP_DURATION + transitionDuration) {
      const progress = (currentTime - CLIP_DURATION) / transitionDuration;
      if (fromVideoRef.current) {
        fromVideoRef.current.currentTime = Math.min(fromVideoDuration - 0.01, fromVideoDuration - 0.01);
        fromVideoRef.current.volume = Math.max(0, 1 - progress);
      }
      if (toVideoRef.current) {
        toVideoRef.current.currentTime = 0;
        toVideoRef.current.volume = Math.min(1, progress);
      }
    } else {
      if (fromVideoRef.current) {
        fromVideoRef.current.volume = 0;
      }
      if (toVideoRef.current) {
        const videoTime = currentTime - (CLIP_DURATION + transitionDuration);
        toVideoRef.current.currentTime = Math.min(videoTime, toVideoDuration - 0.01);
        toVideoRef.current.volume = 1;
      }
    }
  }, [currentTime, fromVideoDuration, toVideoDuration, CLIP_DURATION, transitionDuration, useCompositor]);

  const handleTogglePlay = useCallback(() => {
    if (useCompositor && compositorRef.current) {
      compositorRef.current.toggle();
      setIsPlaying(compositorRef.current.isPlaying());
    } else {
      lastTimeRef.current = 0;
      setIsPlaying(prev => !prev);
    }
  }, [useCompositor]);

  const handleReset = useCallback(() => {
    setCurrentTime(0);
    setIsPlaying(true);
    lastTimeRef.current = 0;
    if (useCompositor && compositorRef.current) {
      compositorRef.current.seekTo(0);
      compositorRef.current.play();
    }
  }, [useCompositor]);

  const handleSeek = useCallback((value: number) => {
    setCurrentTime(value);
    lastTimeRef.current = 0;
    if (useCompositor && compositorRef.current) {
      compositorRef.current.seekToSeconds(value);
    }
  }, [useCompositor]);

  const handleCompositorTimeUpdate = useCallback((timeMs: number) => {
    setCurrentTime(timeMs / 1000);
  }, []);

  const handleCompositorReady = useCallback(() => {
    logger.info('[TransitionStudio] Compositor ready');
    setCompositorReady(true);
  }, []);

  const handleCompositorEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const getEffectiveMaxDuration = (type: TransitionType): number => {
    const typeMax = MAX_TRANSITION_DURATIONS[type];
    const beforeSeconds = beforeSceneDuration ? (beforeSceneDuration / 1000) * 0.9 : Infinity;
    const afterSeconds = afterSceneDuration ? (afterSceneDuration / 1000) * 0.9 : Infinity;
    const sceneBasedMax = Math.min(beforeSeconds, afterSeconds);
    const effectiveMax = Math.min(typeMax, sceneBasedMax);
    return Math.max(MIN_TRANSITION_DURATIONS[type], effectiveMax);
  };

  const handleTypeChange = (type: TransitionType) => {
    const typeDefault = DEFAULT_TRANSITION_DURATIONS[type];
    const typeEffectiveMax = getEffectiveMaxDuration(type);
    const newDuration = Math.min(typeDefault, typeEffectiveMax);
    setLocalTransition({
      ...localTransition,
      type,
      duration: newDuration,
    });
    handleReset();
  };

  const handleDurationChange = (duration: number) => {
    const validatedDuration = validateTransitionDuration(localTransition.type, duration);
    setLocalTransition({
      ...localTransition,
      duration: validatedDuration,
    });
    handleReset();
  };

  const handleApply = () => {
    onChange(localTransition);
    onClose();
  };

  const handleApplyToAllClick = () => {
    if (onApplyToAll) {
      onApplyToAll(localTransition);
      onClose();
    }
  };

  const handleApplyToRemainingClick = () => {
    if (onApplyToRemaining) {
      onApplyToRemaining(localTransition);
      onClose();
    }
  };

  useEffect(() => {
    if (!visible) return;
    const handleKeyPress = (e: KeyboardEvent) => {
      if (showPropertyDock && (e.target as HTMLElement)?.closest('.property-dock')) return;
      
      switch (e.key) {
        case ' ':
          e.preventDefault();
          handleTogglePlay();
          break;
        case 'r':
          e.preventDefault();
          handleReset();
          break;
        case 'Escape':
          if (showPropertyDock) {
            setShowPropertyDock(false);
          } else {
            onClose();
          }
          break;
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [visible, onClose, handleTogglePlay, handleReset, showPropertyDock]);

  const getVisualState = () => {
    if (currentTime < CLIP_DURATION) {
      return { fromOpacity: 1, toOpacity: 0, fromTransform: 'translateX(0)', toTransform: 'translateX(0)', phase: 'from' };
    }
    
    if (currentTime < CLIP_DURATION + transitionDuration) {
      const progress = (currentTime - CLIP_DURATION) / transitionDuration;
      let fromOpacity = 1, toOpacity = 0, fromTransform = 'translateX(0)', toTransform = 'translateX(0)';
      
      switch (localTransition.type) {
        case 'cut':
          fromOpacity = progress < 1 ? 1 : 0;
          toOpacity = progress >= 1 ? 1 : 0;
          break;
        case 'fade':
          fromOpacity = progress < 0.5 ? 1 - (progress * 2) : 0;
          toOpacity = progress >= 0.5 ? ((progress - 0.5) * 2) : 0;
          break;
        case 'crossfade':
          fromOpacity = 1 - progress;
          toOpacity = progress;
          break;
        case 'slide':
          fromOpacity = 1;
          toOpacity = 1;
          fromTransform = `translateX(-${progress * 100}%)`;
          toTransform = `translateX(${(1 - progress) * 100}%)`;
          break;
      }
      return { fromOpacity, toOpacity, fromTransform, toTransform, phase: 'transition' };
    }
    
    return { fromOpacity: 0, toOpacity: 1, fromTransform: 'translateX(0)', toTransform: 'translateX(0)', phase: 'to' };
  };

  const visualState = getVisualState();
  const effectiveMaxDuration = getEffectiveMaxDuration(localTransition.type);
  const formatTime = (s: number | undefined) => `${(s ?? 0).toFixed(1)}s`;

  const renderVideoPreview = () => {
    if (useCompositor && compositorDataReady && fromRecordingData && toRecordingData) {
      return (
        <div className="compositor-container">
          <TransitionCompositorPlayer
            ref={compositorRef}
            fromRecordingData={fromRecordingData}
            toRecordingData={toRecordingData}
            fromPendingBlobUrls={fromPendingBlobUrls}
            toPendingBlobUrls={toPendingBlobUrls}
            transition={localTransition}
            clipDurationSeconds={CLIP_DURATION}
            useFullScenes={true}
            autoPlay={false}
            loop={false}
            showControls={true}
            onTimeUpdate={handleCompositorTimeUpdate}
            onReady={handleCompositorReady}
            onEnded={handleCompositorEnded}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    }

    return (
      <>
        <div 
          className="video-layer"
          style={{
            opacity: visualState.fromOpacity,
            transform: visualState.fromTransform,
            zIndex: visualState.fromOpacity > visualState.toOpacity ? 2 : 1,
          }}
        >
          {fromVideoUrl ? (
            <video
              ref={fromVideoRef}
              src={fromVideoUrl}
              playsInline
              onLoadedMetadata={(e) => setFromVideoDuration(e.currentTarget.duration)}
            />
          ) : (
            <div className="placeholder-scene from">
              <Text>{fromScene?.title || 'Scene 1'}</Text>
            </div>
          )}
        </div>

        <div 
          className="video-layer"
          style={{
            opacity: visualState.toOpacity,
            transform: visualState.toTransform,
            zIndex: visualState.toOpacity >= visualState.fromOpacity ? 2 : 1,
          }}
        >
          {toVideoUrl ? (
            <video
              ref={toVideoRef}
              src={toVideoUrl}
              playsInline
              onLoadedMetadata={(e) => setToVideoDuration(e.currentTarget.duration)}
            />
          ) : (
            <div className="placeholder-scene to">
              <Text>{toScene?.title || 'Scene 2'}</Text>
            </div>
          )}
        </div>
      </>
    );
  };

  const showLoadingState = useCompositor 
    ? ((!compositorDataReady || !compositorReady) && visible && bothScenesRecorded) 
    : (isLoading && bothScenesRecorded);

  const renderMissingScenesMessage = () => {
    const fromHasRecording = sceneHasRecording(fromScene);
    const toHasRecording = sceneHasRecording(toScene);
    
    const missingScenes: string[] = [];
    if (!fromHasRecording) missingScenes.push(fromScene?.title || 'Scene 1');
    if (!toHasRecording) missingScenes.push(toScene?.title || 'Scene 2');
    
    return (
      <div className="loading-state" style={{ 
        flexDirection: 'column', 
        gap: 16, 
        textAlign: 'center',
        padding: 32 
      }}>
        <div style={{ fontSize: 48, opacity: 0.5 }}>🎬</div>
        <Text strong style={{ fontSize: 16 }}>
          {missingScenes.length === 2 
            ? 'Both scenes need to be recorded' 
            : `"${missingScenes[0]}" needs to be recorded`}
        </Text>
        <Text type="secondary" style={{ maxWidth: 300 }}>
          Record both scenes first to preview the transition between them.
          You can still configure the transition settings.
        </Text>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      className="transition-studio-modal"
      closable={false}
      destroyOnHidden
    >
      <div className="studio-container">
        <div className={`preview-area ${showPropertyDock ? 'dock-open' : ''}`}>
          <div className="preview-header">
            <div className="scene-context">
              <span className="scene-label">{fromScene?.title || 'Scene 1'}</span>
              <ChevronRight size={16} className="arrow-icon" />
              <span className="scene-label">{toScene?.title || 'Scene 2'}</span>
            </div>
            <div className="header-actions">
              <Button
                type="text"
                icon={<Settings size={18} />}
                className={`settings-btn ${showPropertyDock ? 'active' : ''}`}
                onClick={() => setShowPropertyDock(!showPropertyDock)}
              >
                Edit Transition
              </Button>
              <Button
                type="text"
                icon={<X size={18} />}
                className="close-btn"
                onClick={onClose}
              />
            </div>
          </div>

          <div className="video-container">
            {!bothScenesRecorded ? (
              renderMissingScenesMessage()
            ) : (
              <>
                {showLoadingState && (
                  <div className="loading-state">
                    <Spin size="large" />
                    <Text type="secondary">Loading videos...</Text>
                  </div>
                )}
                <div style={{ 
                  visibility: showLoadingState ? 'hidden' : 'visible',
                  position: showLoadingState ? 'absolute' : 'relative',
                  width: '100%',
                  height: '100%',
                }}>
                  {renderVideoPreview()}
                  <div className="transition-badge">
                    {getTransitionIcon(localTransition.type)}
                    <span>{TRANSITION_OPTIONS.find(o => o.value === localTransition.type)?.label}</span>
                    {localTransition.type !== 'cut' && (
                      <span className="duration">{localTransition.duration?.toFixed(1)}s</span>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {!useCompositor && (
            <div className="floating-controls">
              <Button
                type="text"
                icon={isPlaying ? <Pause size={20} /> : <Play size={20} fill="white" />}
                className="play-btn"
                onClick={handleTogglePlay}
              />
              <Button
                type="text"
                icon={<RotateCcw size={16} />}
                className="reset-btn"
                onClick={handleReset}
              />
              <div className="scrubber">
                <Slider
                  min={0}
                  max={totalDuration}
                  step={0.01}
                  value={currentTime}
                  onChange={handleSeek}
                  tooltip={{ formatter: formatTime }}
                />
              </div>
              <Text className="time-display">{formatTime(currentTime)} / {formatTime(totalDuration)}</Text>
            </div>
          )}
        </div>

        <div className={`property-dock ${showPropertyDock ? 'open' : ''}`}>
          <div className="dock-header">
            <Text strong>Transition Settings</Text>
          </div>

          <div className="dock-content">
            <div className="section">
              <Text className="section-label">Type</Text>
              <div className="transition-type-list">
                {TRANSITION_OPTIONS.map(option => (
                  <div
                    key={option.value}
                    className={`type-option ${localTransition.type === option.value ? 'selected' : ''}`}
                    onClick={() => !disabled && handleTypeChange(option.value)}
                  >
                    <div className="option-icon">{option.icon}</div>
                    <div className="option-text">
                      <Text strong>{option.label}</Text>
                      <Text type="secondary" className="option-desc">{option.description}</Text>
                    </div>
                    {localTransition.type === option.value && (
                      <Check size={16} className="check-icon" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {localTransition.type !== 'cut' && (
              <div className="section">
                <Text className="section-label">Duration</Text>
                <div className="duration-control">
                  <div className="duration-value">
                    <span className="number">{(localTransition.duration ?? 0).toFixed(1)}</span>
                    <span className="unit">s</span>
                  </div>
                  <Slider
                    min={MIN_TRANSITION_DURATIONS[localTransition.type]}
                    max={effectiveMaxDuration}
                    step={0.1}
                    value={Math.min(localTransition.duration ?? 0, effectiveMaxDuration)}
                    onChange={handleDurationChange}
                    disabled={disabled}
                  />
                  <div className="slider-labels">
                    <Text type="secondary">{MIN_TRANSITION_DURATIONS[localTransition.type]}s</Text>
                    <Text type="secondary">{effectiveMaxDuration.toFixed(1)}s</Text>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="dock-footer">
            <div className="action-buttons">
              {onApplyToRemaining && !isLastTransition && (
                <Button onClick={handleApplyToRemainingClick} disabled={disabled}>
                  Apply To Remaining
                </Button>
              )}
              {onApplyToAll && (
                <Button onClick={handleApplyToAllClick} disabled={disabled}>
                  Apply To All
                </Button>
              )}
            </div>
            <Button type="primary" onClick={handleApply} disabled={disabled}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TransitionStudioModal;
